// routes/_app.$usernameRaw.models.($subTab).tsx
//
// `/{lang}/@username/models[/:subTab]` — a legacy URL that
// `UserProfileContent` already redirects to its modern location
// `/{lang}/@username/artworks/models[/:subTab]` via `redirRootTab` /
// `<Navigate replace />`. So the previous CSR-only implementation
// (`dynamic({ssr:false})` `UserProfileContent` behind a
// `<UserProfileShell>` Suspense fallback) only ever rendered the
// shell on the server, then mounted the real component on the
// client just long enough for it to fire the SPA redirect to
// `/artworks/models`. Net result: SSR HTML disagreed with the page
// the user actually ended up on (shell vs full model list), and the
// SEO meta we computed in the loader was for the wrong URL.
//
// New behaviour: respond with a server-side `302` straight to the
// canonical artworks/models path, preserving the optional `:subTab`
// segment and any `?query` / `#hash`. Crawlers follow the redirect
// to the page that actually contains the indexable content, no
// shell HTML is ever shipped, and the loader skips the wasted
// `fetchUserForSSR` + `getSeoConfig` round-trip.
import { redirect } from 'react-router'
import { userProfileHandle, userProfileLoader } from 'src/utils/router/route'
import type { Route } from './+types/_app.$usernameRaw.models.($subTab)'

export const handle = userProfileHandle

/**
 * Build the canonical `/{lang}/@username/artworks/models[/subTab]` URL,
 * preserving any optional `:subTab` segment, query, and hash. See the
 * matching helper on the `manga` route for the rationale behind the
 * pathname-rewrite approach.
 */
function buildArtworksModelsRedirectUrl(request: Request): string {
  const url = new URL(request.url)
  url.pathname = url.pathname.replace(
    /^(\/.*?\/@[^/]+)\/models(\/|$)/,
    '$1/artworks/models$2',
  )
  return `${url.pathname}${url.search}${url.hash}`
}

export async function loader({ request }: Route.LoaderArgs) {
  throw redirect(buildArtworksModelsRedirectUrl(request))
}

export async function clientLoader({
  request,
  params,
}: Route.ClientLoaderArgs) {
  await userProfileLoader({
    request,
    params: { ...params, tab: 'models' },
  })
  throw redirect(buildArtworksModelsRedirectUrl(request))
}
clientLoader.hydrate = true

/**
 * Placeholder element — never actually mounted because both `loader`
 * and `clientLoader` `throw redirect(...)` before React Router gets a
 * chance to render this route. The CSR data router
 * (`src/data-router/routes.tsx`) still references `module.default` as
 * the route's `element`, so we have to export *something*.
 */
export default function UserModelsRedirect() {
  return null
}
